import {Icon} from '@/app/components/Icon';
import React, {useRef, useState} from 'react';
import ReactCrop, {centerCrop, makeAspectCrop, PixelCrop} from 'react-image-crop';
import {UserAvatar} from '@/components/app/feedback';
import {Button} from '@/components/app/button';
import {Modal} from '@/components/app/modal';
import {AvatarActions} from '@/components/app/navigation';
import {cn} from '@/lib/utils';
import {useAppSelector} from '../../../app/hooks/useAppSelector';
import {useGetAvatarQuery} from '../../../app/services/identitiesApi';
import {selectSession} from '../../auth/authSlice';
import {canvasPreview} from '../utils';
import 'react-image-crop/dist/ReactCrop.css';
const PROFILE_IMAGE_SIZE = 100;
export interface ProfileImageProps {
  avatarClassName?: string;
  editable?: boolean;
  onClickAvatar?: (e: React.MouseEvent<HTMLElement>) => void;
  onEdit?: (event: {base64: string}) => void;
}
export const ProfileImage = ({avatarClassName, editable, onClickAvatar, onEdit}: ProfileImageProps) => {
  const session = useAppSelector(selectSession);
  const [profileImageSrc, setProfileImageSrc] = useState('');
  const [profileImageDialogVisible, setProfileImageDialogVisible] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const profileImage = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<PixelCrop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const {data: avatar, isFetching} = useGetAvatarQuery();
  return (
    <>
      <div className="relative w-fit rounded-full">
        <UserAvatar
          className={cn('bg-zinc-300 text-foreground flex', avatarClassName)}
          icon={isFetching ? 'spinner' : undefined}
          image={avatar?.avatarImageBase64 ?? ''}
          label={`${session?.name
            .split(' ')
            .map((e: string) => e[0])
            .join('')}`}
          onClick={onClickAvatar}
          shape="circle"
        />
        {editable && (
          <>
            {!avatar?.avatarImageBase64 && (
              <Button
                className="absolute bottom-0 right-0 bg-primary text-primary-foreground rounded-full text-sm h-[2rem] w-[2rem]"
                onClick={() => fileInput.current?.click()}
                type="button"
                startIcon={<Icon icon={'plus'} />}
                aria-label={'Add'}
                size="icon"
              />
            )}
            {avatar?.avatarImageBase64 && (
              <AvatarActions
                className="absolute bottom-0 left-full -ml-8"
                icon="bars"
                actions={[
                  {
                    icon: 'pencil',
                    onSelect: () => fileInput.current?.click(),
                  },
                  {
                    icon: 'trash',
                    onSelect: () =>
                      onEdit?.({
                        base64: '',
                      }),
                  },
                ]}
              />
            )}
            <input
              className="absolute opacity-0"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  setCrop(undefined); // Makes crop preview update between images.
                  const reader = new FileReader();
                  reader.addEventListener('load', () => {
                    setProfileImageSrc(reader.result?.toString() || '');
                    setProfileImageDialogVisible(true);
                  });
                  reader.readAsDataURL(e.target.files[0]);
                }
                e.target.value = '';
              }}
              ref={fileInput}
              type="file"
            />
          </>
        )}
      </div>
      <Modal
        contentClassName="p-0 rounded-md"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="text"
              className="rounded-full bg-transparent"
              onClick={() => setProfileImageDialogVisible(false)}
              type="button"
            >
              {'Cancel'}
            </Button>
            <Button className="rounded-full" onClick={() => save()} type="button">
              {'Save'}
            </Button>
          </div>
        }
        onClose={() => setProfileImageDialogVisible(false)}
        showHeader={false}
        open={profileImageDialogVisible}
      >
        {!!profileImageSrc && (
          <ReactCrop
            aspect={1}
            className="block"
            crop={crop}
            minHeight={PROFILE_IMAGE_SIZE}
            minWidth={PROFILE_IMAGE_SIZE}
            onChange={(crop) => setCrop(crop)}
            onComplete={(c) => setCompletedCrop(c)}
          >
            <img
              alt="Crop me"
              ref={profileImage}
              src={profileImageSrc}
              style={{
                maxHeight: '80vh',
              }}
              onLoad={onImageLoad}
            />
          </ReactCrop>
        )}
      </Modal>
    </>
  );
  async function save() {
    if (profileImage.current && completedCrop) {
      const canvas = document.createElement('canvas');
      canvas.style.aspectRatio = '1';
      await canvasPreview(profileImage.current!, canvas, completedCrop);
      const canvasScaled = document.createElement('canvas');
      canvasScaled.height = PROFILE_IMAGE_SIZE;
      canvasScaled.width = PROFILE_IMAGE_SIZE;
      canvasScaled
        .getContext('2d')
        ?.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, canvasScaled.width, canvasScaled.height);
      setProfileImageDialogVisible(false);
      onEdit?.({
        base64: canvasScaled.toDataURL(),
      });
    }
  }
  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const {width, height} = e.currentTarget;
    setCrop(centerAspectCrop(width, height, 1));
  }
  function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number) {
    return centerCrop(
      makeAspectCrop(
        {
          unit: 'px',
          width: PROFILE_IMAGE_SIZE,
        },
        aspect,
        PROFILE_IMAGE_SIZE,
        PROFILE_IMAGE_SIZE,
      ),
      mediaWidth,
      mediaHeight,
    );
  }
};
